/*
 * Atlas Tea Co. — Cart Recently Viewed
 * assets/atlas-cart-recently-viewed.js
 *
 * Drives <atlas-cart-recently-viewed> (sections/atlas-cart-recently-viewed.liquid).
 * Adapted from assets/recently-viewed-products.js — same localStorage source and
 * Swiper setup — with three differences for the cart's fallback model:
 *
 *   1. Reads the browsing history from localStorage ("minimog-recently-viewed").
 *      If there is NO history, it does not fetch: the server-rendered fallback
 *      collection is left in place (and its carousel is still initialised).
 *   2. When there IS history, it fetches this section through the Search
 *      section-render endpoint and only swaps the fallback out when the fetch
 *      actually returns cards (count of .atlas-pr__col > 0).
 *   3. Card counting / arrow-offset selectors target the Atlas card markup
 *      (.atlas-pr__col / .atlas-pr__card-image) rather than Minimog's defaults.
 *
 * Relies on the same globals as recently-viewed-products.js: queryDomNodes,
 * MinimogTheme, MinimogLibs (all defined by the theme bundles).
 */
if (!customElements.get("atlas-cart-recently-viewed")) {
  class AtlasCartRecentlyViewed extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
      };

      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.productsPerRow;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.productsToshow = parseInt(this.dataset.productsToShow);

      this.init();
    }

    init() {
      const query = this.getQueryString();

      // No browsing history → keep the server-rendered fallback collection and
      // just init its carousel (if any). Nothing to fetch.
      if (!query) {
        this.setupFallback();
        return;
      }

      fetch(this.dataset.url + query)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const rendered = html.querySelector("atlas-cart-recently-viewed");
          const count = rendered ? rendered.querySelectorAll(".atlas-pr__col").length : 0;

          // Only replace the fallback when the history fetch returned cards;
          // otherwise (all viewed products now unavailable) leave it untouched.
          if (rendered && count > 0 && rendered.innerHTML.trim().length) {
            this.innerHTML = rendered.innerHTML;
            this.classList.remove("m:hidden");
            this.products = count;
            this.bindScreenSize();

            MinimogTheme.CompareProduct?.setCompareButtonsState();
            MinimogTheme.Wishlist?.setWishlistButtonsState();
          } else {
            this.setupFallback();
          }
        })
        .catch((e) => {
          console.error(e);
          this.setupFallback();
        });
    }

    // Initialise the carousel over whatever is already in the DOM (the
    // server-rendered fallback collection).
    setupFallback() {
      this.products = this.querySelectorAll(".atlas-pr__col").length;
      if (this.products > 0) this.bindScreenSize();
    }

    bindScreenSize() {
      this.initByScreenSize();
      document.addEventListener("matchMobile", () => this.initByScreenSize());
      document.addEventListener("unmatchMobile", () => this.initByScreenSize());
    }

    initByScreenSize() {
      if (!this.enableSlider) return;
      const { slideContainer, slideControls } = queryDomNodes(this.selectors, this);

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        slideControls && slideControls.classList.add("m:hidden");
        slideContainer && slideContainer.classList.remove("swiper-container");
        if (this.swiper) this.swiper.destroy(false, true);
      } else {
        slideControls && slideControls.classList.remove("m:hidden");
        this.initSlider();
      }

      if (this.products <= parseInt(this.items)) {
        slideControls && slideControls.classList.add("m:hidden");
      }
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);
      if (!slideContainer) return;
      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const swiperWrapper = slideContainer.querySelector(".swiper-wrapper");
      const slideItemsLength = swiperWrapper ? swiperWrapper.childElementCount : 0;

      slideContainer.classList.add("swiper-container");

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.products <= parseInt(this.items) ? false : true,
        pagination: this.showPagination
          ? {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
          }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          1024: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: () => {
            setTimeout(() => {
              // Vertically centre the hover arrows against the card image.
              const firstItem =
                this.querySelector(".atlas-pr__card-image") ||
                this.querySelector(".m-image") ||
                this.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton && prevButton.classList.remove("m:hidden");
                nextButton && nextButton.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => this.slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => this.slider.slideNext());
      }

      this.swiper = slideContainer && slideContainer.swiper;
    }

    getQueryString() {
      const cookieName = "minimog-recently-viewed";
      const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
      if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
        items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
      }
      return items
        .map((item) => "id:" + item)
        .slice(0, this.productsToshow)
        .join(" OR ");
    }
  }

  customElements.define("atlas-cart-recently-viewed", AtlasCartRecentlyViewed);
}
